<div class="min-h-screen bg-white">
  <!-- Header with back button -->
  <div class="bg-primary-light py-4 px-4 lg:px-0">
    <div class="container mx-auto">
      <button (click)="goBack()" class="flex items-center gap-2 text-gray-700 hover:text-main-600 transition">
        <icon-arrow-left [size]="20" />
        <span class="font-medium">{{ 'HAIRSTYLE_DETAILS.BACK_TO_HAIRSTYLES' | translate }}</span>
      </button>
    </div>
  </div>

  @if (isLoadingHairstyle()) {
  <braidsbook-hairstyle-details-skeleton />
  } @else if (hairstyle()) {
  @let currentHairstyle = hairstyle()!;

  <!-- Hairstyle Details Section -->
  <div class="container mx-auto py-8 lg:py-12 px-4">
    <!-- Images Gallery -->
    <div class="mb-8">
      @if (currentHairstyle.images && currentHairstyle.images.length > 0) {
      <div class="relative group">
        <!-- Navigation Buttons -->
        @if (currentHairstyle.images.length > 1) {
        <button
          class="hairstyle-swiper-prev absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/90 hover:bg-white text-main-600 rounded-full shadow-lg transition-all duration-200 hover:scale-110 opacity-0 group-hover:opacity-100"
          [attr.aria-label]="'HAIRSTYLE_DETAILS.PREVIOUS_IMAGE' | translate">
          <icon-arrow-left [size]="24" />
        </button>
        <button
          class="hairstyle-swiper-next absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/90 hover:bg-white text-main-600 rounded-full shadow-lg transition-all duration-200 hover:scale-110 opacity-0 group-hover:opacity-100"
          [attr.aria-label]="'HAIRSTYLE_DETAILS.NEXT_IMAGE' | translate">
          <icon-arrow-right [size]="24" />
        </button>
        }

        <!-- Swiper Container -->
        <swiper-container class="w-full" braidsbookSwiper [swiperOptions]="swiperOptions" init="false">
          @for (image of currentHairstyle.images; track $index) {
          <swiper-slide>
            <div class="relative h-64 md:h-80 lg:h-96 rounded-xl overflow-hidden border-2 border-main-500">
              <img [ngSrc]="image || IMAGE_PLACEHOLDER" [alt]="currentHairstyle.nom + ' - Image ' + ($index + 1)" fill
                class="object-cover object-top">
            </div>
          </swiper-slide>
          }
        </swiper-container>
      </div>
      } @else {
      <div
        class="relative h-64 lg:h-96 rounded-xl overflow-hidden border-2 border-main-500 bg-primary-light flex items-center justify-center">
        <icon-customer [size]="60" class="text-gray-400" />
      </div>
      }
    </div>

    <!-- Hairstyle Info -->
    <div class="mb-12">
      <h1 class="text-3xl lg:text-4xl font-semibold text-main-900 mb-3">{{ currentHairstyle.nom }}</h1>
      <div class="flex flex-wrap items-center gap-6 mb-6">
        @if (currentHairstyle.temps_moy) {
        <div class="flex items-center gap-2">
          <icon-clock [size]="24" class="text-main-600" />
          <span class="text-xl text-main-700">{{ currentHairstyle.temps_moy }}</span>
        </div>
        }
      </div>

      @if (currentHairstyle.description) {
      <div class="prose max-w-none">
        <p class="text-gray-700 leading-relaxed">{{ currentHairstyle.description }}</p>
      </div>
      }
    </div>

    <!-- Hairdressers Section -->
    <div class="border-t border-gray-200 pt-8">
      <h2 class="text-2xl lg:text-3xl font-semibold text-main-900 mb-6">
        {{ 'HAIRSTYLE_DETAILS.HAIRDRESSERS_OFFERING_TITLE' | translate }}
      </h2>

      @if (isLoadingHairdressers()) {
      <div class="grid grid-cols-2 gap-x-1.5 gap-y-4" [ngClass]="getGridClasses()">
        @for (item of [1, 2, 3, 4, 5, 6, 7, 8]; track $index) {
        <div class="text-sm overflow-hidden lg:border rounded-xl lg:p-1 shadow-lg lg:shadow-none">
          <div class="h-48 md:h-56 xl:h-64 w-auto rounded-lg bg-gray-200 animate-pulse"></div>
          <div class="p-2 flex items-center gap-2">
            <div class="w-8 h-8 lg:w-9 lg:h-9 aspect-square rounded-full bg-gray-200 animate-pulse"></div>
            <div class="flex flex-col gap-1 flex-1">
              <div class="h-3 bg-gray-200 rounded animate-pulse w-20"></div>
              <div class="h-2 bg-gray-200 rounded animate-pulse w-16"></div>
            </div>
          </div>
        </div>
        }
      </div>
      } @else if (hairdressersResponse().data.length === 0) {
      <div class="min-h-64 flex flex-col items-center justify-center py-12">
        <div class="relative h-14 lg:h-16 w-full">
          <img ngSrc="/icons/not-found-icon.svg" alt="Not Found" fill priority class="w-14 lg:w-16">
        </div>
        <div class="text-lg mt-4">{{ 'HAIRSTYLE_DETAILS.NO_HAIRDRESSERS_FOUND' | translate }}</div>
        <div class="text-gray-600">{{ 'HAIRSTYLE_DETAILS.NO_HAIRDRESSERS_MESSAGE' | translate }}</div>
      </div>
      } @else {
      <div class="grid grid-cols-2 gap-x-4 gap-y-6" [ngClass]="getGridClasses()">
        @for (hairdresser of hairdressersResponse().data | paginate:{
        itemsPerPage: hairdressersResponse().limit,
        totalItems: hairdressersResponse().totalElements,
        currentPage: hairdressersResponse().currentPage
        }; track $index) {
        <braidsbook-hairdresser-card [hairdresser]="hairdresser" />
        }
      </div>

      @if (hairdressersResponse().totalPages > 1) {
      <pagination-controls [nextLabel]="'HAIRDRESSERS.NEXT' | translate"
        [previousLabel]="'HAIRDRESSERS.PREVIOUS' | translate" (pageChange)="onPageChange($event)" class="pagination" />
      }
      }
    </div>
  </div>
  } @else {
  <div class="container mx-auto py-10 px-4">
    <div class="min-h-64 flex flex-col items-center justify-center">
      <div class="relative h-14 lg:h-16 w-full">
        <img ngSrc="/icons/not-found-icon.svg" alt="Not Found" fill priority class="w-14 lg:w-16">
      </div>
      <div class="text-lg mt-4">{{ 'HAIRSTYLE_DETAILS.HAIRSTYLE_NOT_FOUND' | translate }}</div>
      <button (click)="goBack()"
        class="mt-4 px-6 py-2 bg-main-500 text-white rounded-full hover:bg-main-600 transition">
        {{ 'HAIRSTYLE_DETAILS.GO_BACK' | translate }}
      </button>
    </div>
  </div>
  }
</div>